import { planManagementService } from './plan-management.service';

import * as atlas from 'azure-maps-control';
import proj4 from 'proj4';


// Use higher precision rounding without `toFixed()`
const preciseRound = (value: number, decimals = 10) => {
  return Math.round(value * Math.pow(10, decimals)) / Math.pow(10, decimals);
};

export class MapService {

  // Private static instance of the class (singleton)
  private static instance: MapService;
  private mapInstance: atlas.Map | null = null;
  private _bboxDataSource: atlas.source.DataSource | null = null;
  private _bboxLayer: atlas.layer.LineLayer | null = null;

  // Private constructor to prevent direct instantiation
  private constructor() {
    proj4.defs("EPSG:4326", "+proj=longlat +datum=WGS84 +no_defs");
    proj4.defs("EPSG:28992", "+proj=sterea +lat_0=52.15616055555555 +lon_0=5.38763888888889 +k=0.9999079 +x_0=155000 +y_0=463000 +ellps=bessel +towgs84=565.4171,50.3319,465.5524,-0.398957,0.343988,-1.8774,4.0725 +units=m +no_defs");
  }

  // Private constructor to prevent direct instantiation
  public setMapInstance(map: atlas.Map | null = null) {
    this.mapInstance = map;
  }

  public getMapInstance() {
    if (!this.mapInstance) {
        console.warn("Map instance not initialized yet.");
    }
    return this.mapInstance;
  }

  // Method to return the singleton instance
  public static getInstance(): MapService {
    if (!MapService.instance) {
      MapService.instance = new MapService();
    }
    return MapService.instance;
  }

  public isValidBbox(bbox: unknown): bbox is [number, number, number, number] {
    return Array.isArray(bbox)
      && bbox.length === 4
      && bbox.every(value => typeof value === 'number' && Number.isFinite(value));
  }

  // Function to calculate centroid of a polygon
  public calculateCentroid(coordinates: number[][] | number[][][]): number[] {
    // Ensure we are getting the first element if coordinates are deeply nested
    if (Array.isArray(coordinates[0]) && Array.isArray(coordinates[0][0])) {
      coordinates = coordinates[0] as number[][];
    }

    let xSum = 0, ySum = 0, numPoints = coordinates.length;

    coordinates.forEach(([x, y]) => {
      xSum += Number(x);
      ySum += Number(y);
    });

    return [xSum / numPoints, ySum / numPoints];
  }

  // public calculatedBoundingBox = (bboxes: number[][]) => {
  //   if (!bboxes.length) return null;

  //   const min_X = Math.min(...bboxes.map(bbox => bbox[0])); // min Lng / west
  //   const min_Y = Math.min(...bboxes.map(bbox => bbox[1])); // min Lat / south
  //   const max_X = Math.max(...bboxes.map(bbox => bbox[2])); // max Lng / east
  //   const max_Y = Math.max(...bboxes.map(bbox => bbox[3])); // max Lat / north

  //   return [min_X, min_Y, max_X, max_Y];
  // };

  public calculatedBoundingBox = (bboxes: number[][]) => {
    // const map = mapService.getMapInstance();
    
    if (!bboxes.length) return null;

    // Compute min/max bounds from data
    const min_X = Math.min(...bboxes.map(bbox => bbox[0])); // min Lng / west
    const min_Y = Math.min(...bboxes.map(bbox => bbox[1])); // min Lat / south
    const max_X = Math.max(...bboxes.map(bbox => bbox[2])); // max Lng / east
    const max_Y = Math.max(...bboxes.map(bbox => bbox[3])); // max Lat / north

    let bbox = [min_X, min_Y, max_X, max_Y];

    if (this.mapInstance) {
        // const camera = this.mapInstance.getCamera();
        // const mapWidth = camera.width;
        // const mapHeight = camera.height;
        const mapCanvas = this.mapInstance.getCanvas();
        const mapWidth = mapCanvas.clientWidth; // ✅ Correct way to get width
        const mapHeight = mapCanvas.clientHeight; // ✅ Correct way to get height

        if (mapWidth && mapHeight) {
            const aspectRatio = mapWidth / mapHeight;
            bbox = this.expandBBoxToFitAspectRatio(bbox, aspectRatio);
        }
    }

    return bbox;
  };

  private expandBBoxToFitAspectRatio = (bbox: number[], aspectRatio: number) => {
    const [min_X, min_Y, max_X, max_Y] = bbox;

    // Calculate width & height of current bbox
    const bboxWidth = max_X - min_X;
    const bboxHeight = max_Y - min_Y;
    const bboxAspectRatio = bboxWidth / bboxHeight;

    let newMin_X = min_X, newMax_X = max_X;
    let newMin_Y = min_Y, newMax_Y = max_Y;

    // Adjust the bbox to fit the map's aspect ratio
    if (bboxAspectRatio < aspectRatio) {
        // Expand width to match aspect ratio
        const newWidth = bboxHeight * aspectRatio;
        const diff = (newWidth - bboxWidth) / 2;
        newMin_X -= diff;
        newMax_X += diff;
    } 
    else {
        // Expand height to match aspect ratio
        const newHeight = bboxWidth / aspectRatio;
        const diff = (newHeight - bboxHeight) / 2;
        newMin_Y -= diff;
        newMax_Y += diff;
    }

    return [newMin_X, newMin_Y, newMax_X, newMax_Y];
  };

  public convertBboxLatLngToRD(bbox: number[], returnAsArray: boolean = false) {
    const [min_X, min_Y] = proj4("EPSG:4326", "EPSG:28992", [bbox[0], bbox[1]]);
    const [max_X, max_Y] = proj4("EPSG:4326", "EPSG:28992", [bbox[2], bbox[3]]);

    const roundedBBox = [
        preciseRound(min_X, 10), 
        preciseRound(min_Y, 10), 
        preciseRound(max_X, 10), 
        preciseRound(max_Y, 10)
    ];

    return returnAsArray ? roundedBBox : roundedBBox.join(",");
  }

  public convertBboxRDToLatLng(bbox: number[], returnAsArray: boolean = false) {
    const [min_X, min_Y] = proj4("EPSG:28992", "EPSG:4326", [bbox[0], bbox[1]]);
    const [max_X, max_Y] = proj4("EPSG:28992", "EPSG:4326", [bbox[2], bbox[3]]);

    const roundedBBox = [
        preciseRound(min_X, 10), 
        preciseRound(min_Y, 10), 
        preciseRound(max_X, 10), 
        preciseRound(max_Y, 10)
    ];

    return returnAsArray ? roundedBBox : roundedBBox.join(",");
  }


  // public convertBboxLatLngToRD(bbox: number[], returnAsArray: boolean = false) {
  //   const [min_X, min_Y] = proj4("EPSG:4326", "EPSG:28992", [bbox[0], bbox[1]]); // west, south
  //   const [max_X, max_Y] = proj4("EPSG:4326", "EPSG:28992", [bbox[2], bbox[3]]); // east, north

  //   if (returnAsArray) {
  //     return [min_X, min_Y, max_X, max_Y];
  //   }

  //   return `${min_X},${min_Y},${max_X},${max_Y}`;
  // }

  // public convertBboxRDToLatLng(bbox: number[], returnAsArray: boolean = false) {
  //   const [min_X, min_Y] = proj4("EPSG:28992", "EPSG:4326", [bbox[0], bbox[1]]);
  //   const [max_X, max_Y] = proj4("EPSG:28992", "EPSG:4326", [bbox[2], bbox[3]]);

  //   if (returnAsArray) {
  //     return [min_X, min_Y, max_X, max_Y];
  //   }

  //   return `${min_X},${min_Y},${max_X},${max_Y}`;
  // }

  public convertRDToWGS84_OLD(coordinates: any) {
      if (!Array.isArray(coordinates)) {
        console.warn("Invalid input: coordinates is not an array", coordinates);
        return [];
      }

      return coordinates.map((polygonOrRing) => {
        if (!Array.isArray(polygonOrRing)) {
          console.warn("Invalid input: polygonOrRing is not an array", polygonOrRing);
          return [];
        }

        return polygonOrRing.map((ringOrPoint) => {
          if (!Array.isArray(ringOrPoint) || ringOrPoint.length === 0) {
            console.warn("Invalid input: ringOrPoint is not an array or empty", ringOrPoint);
            return [];
          }

          return Array.isArray(ringOrPoint[0]) // Detect if it's a polygon ring or a single point
            ? ringOrPoint.map(([x, y]) => {
                if (typeof x !== "number" || typeof y !== "number") {
                  console.warn("Invalid coordinates:", x, y);
                  return [0, 0]; // Default value or handle accordingly
                }
                return proj4("EPSG:28992", "EPSG:4326", [x, y]);
              })
            : proj4("EPSG:28992", "EPSG:4326", ringOrPoint);
        });
      });
  };

  public convertRDToWGS84(coordinates: any): any {
    if (!Array.isArray(coordinates)) {
      console.warn("Invalid input: coordinates is not an array", coordinates);
      return [];
    }

    // Leaf: single coordinate pair [x, y]
    if (coordinates.length >= 2 && typeof coordinates[0] === 'number' && typeof coordinates[1] === 'number') {
      return proj4("EPSG:28992", "EPSG:4326", coordinates);
    }

    // Recurse into nested arrays (rings, polygons, multipolygons, flat point lists, ...)
    return coordinates.map((item: any) => this.convertRDToWGS84(item));
  }

  // ─── Portfolio Bounding Box ───────────────────────────────────────────────

  private bboxRetryCount = 0;
  private bboxRetryMax = 10;

  public drawPortfolioBbox(bbox: [number, number, number, number], color: string = 'rgba(0, 0, 0, 0.5)'): void {
    this.bboxRetryCount = 0;
    this.drawPortfolioBboxWithRetry(bbox, color);
  }

  private drawPortfolioBboxWithRetry(bbox: [number, number, number, number], color: string): void {
    const map = this.mapInstance;
    if (!map || !bbox) {
      console.warn('drawPortfolioBbox: map or bbox missing');
      return;
    }

    this.clearPortfolioBbox();

    const latLngBbox = this.convertBboxRDToLatLng(bbox, true) as number[];
    let [min_lng, min_lat, max_lng, max_lat] = latLngBbox;

    // Add ~10m padding
    const padding = 0.00009;
    min_lng -= padding;
    min_lat -= padding;
    max_lng += padding;
    max_lat += padding;

    const bboxPolygon = new atlas.data.Feature(
      new atlas.data.Polygon([[
        [min_lng, min_lat],
        [max_lng, min_lat],
        [max_lng, max_lat],
        [min_lng, max_lat],
        [min_lng, min_lat]
      ]]),
      { name: 'Bounding Box' }
    );

    // Validate bbox coordinates
    const isValidBbox = 
      typeof min_lng === 'number' && isFinite(min_lng) && min_lng >= -180 && min_lng <= 180 &&
      typeof min_lat === 'number' && isFinite(min_lat) && min_lat >= -90 && min_lat <= 90 &&
      typeof max_lng === 'number' && isFinite(max_lng) && max_lng >= -180 && max_lng <= 180 &&
      typeof max_lat === 'number' && isFinite(max_lat) && max_lat >= -90 && max_lat <= 90;

    if (!isValidBbox) {
      console.error('Invalid bbox coordinates');
      return;
    }

    const bboxDataSource = new atlas.source.DataSource();
    
    try {
      map.sources.add(bboxDataSource);
      this._bboxDataSource = bboxDataSource;
      bboxDataSource.add(bboxPolygon);

      const bboxLayer = new atlas.layer.LineLayer(bboxDataSource, 'bbox-layer', {
        strokeColor: color,
        strokeWidth: 2,
        strokeDashArray: [5, 5],
      });
      map.layers.add(bboxLayer);
      this._bboxLayer = bboxLayer;
    } catch (error) {
      this.bboxRetryCount++;
      const errorMsg = (error as any)?.message || String(error);
      
      if (this.bboxRetryCount < this.bboxRetryMax) {
        // Exponential backoff: 100ms, 200ms, 300ms, 400ms, etc.
        const delay = Math.min(100 * this.bboxRetryCount, 1000);
        console.log(`Draw bbox retry ${this.bboxRetryCount}/${this.bboxRetryMax} in ${delay}ms because map not ready yet...`);
        setTimeout(() => this.drawPortfolioBboxWithRetry(bbox, color), delay);
      } else {
        console.error(`❌ Failed to draw bbox after ${this.bboxRetryMax} retries`);
      }
    }
  }

  public clearPortfolioBbox(): void {
    const map = this.mapInstance;
    if (!map) return;

    if (this._bboxLayer) {
      map.layers.remove(this._bboxLayer);
      this._bboxLayer = null;
    }
    if (this._bboxDataSource) {
      map.sources.remove(this._bboxDataSource);
      this._bboxDataSource = null;
    }
  }

  // ─── Map Layer Filter Utilities ───────────────────────────────────────────

  public static buildEqualsAnyFilter(field: string, values: string[]): any {
    return ['any', ...values.map(v => ['==', ['get', field], v])];
  }

  public static buildFallbackFilter(field: string, known: string[]): any {
    return [
      'any',
      ['!', ['has', field]],
      ['!', MapService.buildEqualsAnyFilter(field, known)]
    ];
  }

  // ─── Polygon Drawing ──────────────────────────────────────────────────────

  public drawPolygon(coordinates: number[][]): void {
    const map = this.mapInstance;
    if (!map) {
      console.warn("Azure Maps instance not available.");
      return;
    }

    // Convert RD coordinates to WGS84
    let transformedCoordinates = this.convertRDToWGS84(coordinates);

    // Ensure the polygon is closed
    if (
      transformedCoordinates.length > 0 &&
      (transformedCoordinates[0][0] !== transformedCoordinates[transformedCoordinates.length - 1][0] ||
        transformedCoordinates[0][1] !== transformedCoordinates[transformedCoordinates.length - 1][1])
    ) {
      transformedCoordinates.push([...transformedCoordinates[0]]);
    }

    // Compute centroid (middle point) of the polygon
    const centroid = this.calculateCentroid(transformedCoordinates);

    // Create a polygon shape
    const polygon = new atlas.data.Polygon([coordinates]);

    // Create a feature shape with properties
    const polygonFeature = new atlas.Shape(
      new atlas.data.Feature(polygon, {
        color: "rgba(0, 0, 0, 0.5)", // BLACK fill with transparency
      })
    );

    // Add to the data source
    const dataSource = new atlas.source.DataSource();
    
    try {
      map.sources.add(dataSource);
    } catch (error) {
      console.error("Failed to add data source to map:", error);
      return;
    }
    
    dataSource.add(polygonFeature);

    // Create a polygon layer
    const polygonLayer = new atlas.layer.PolygonLayer(dataSource, undefined, {
      fillColor: "rgba(0, 0, 0, 0.3)", // BLACK fill with transparency
      strokeColor: "blue",
      strokeWidth: 2,
    });

    map.layers.add(polygonLayer);

    // Add marker (symbol layer) at the centroid
    dataSource.add(new atlas.data.Point(centroid));

    const symbolLayer = new atlas.layer.SymbolLayer(dataSource, undefined, {
      iconOptions: {
        image: "marker-blue",
        allowOverlap: true,
        size: 0.8,
      },
    });

    map.layers.add(symbolLayer);
  }

  // ─── Map Initialization ───────────────────────────────────────────────────

  public async initializeMap(mapContainer: HTMLDivElement): Promise<atlas.Map> {
    const anyCoordinates = [4.5081147, 52.10353492]; // This is Leiden

    let cachedToken: string | null = null;
    let tokenExpireTime: number | null = null;

    // Initialize the map
    const map = new atlas.Map(mapContainer, {
      center: anyCoordinates,
      zoom: 11,
      language: 'nl-NL',
      style: 'grayscale_light',
      authOptions: {
        authType: atlas.AuthenticationType.sas,
        getToken: async (resolve, reject) => {
          try {
            const now = new Date().getTime();

            // Check if cached token is still valid (refresh 60 sec before expiry)
            if (cachedToken && tokenExpireTime && now < tokenExpireTime - 60000) {
              resolve(cachedToken);
              return;
            }

            const response = await planManagementService.getAzureMapsToken();

            cachedToken = response.token;
            tokenExpireTime = new Date(response.expiresAtUtc).getTime();

            resolve(cachedToken);
          } catch (error) {
            console.error("Azure Maps token ophalen mislukt:", error);
            reject(error);
          }
        }
      },
    });

    // Add Fullscreen control
    map.controls.add(new atlas.control.FullscreenControl(), {
      position: atlas.ControlPosition.BottomLeft
    });

    // Add Zoom control
    map.controls.add(new atlas.control.ZoomControl(), {
      position: atlas.ControlPosition.BottomLeft,
    });

    // Add pitch control
    map.controls.add(new atlas.control.PitchControl(), {
      position: atlas.ControlPosition.BottomLeft,
    });

    // Add compass control
    map.controls.add(new atlas.control.CompassControl(), {
      position: atlas.ControlPosition.BottomLeft,
    });

    // Add style control
    map.controls.add(new atlas.control.StyleControl({
        mapStyles: ['road', 'grayscale_light', 'grayscale_dark', 'night', 'road_shaded_relief', 'satellite', 'satellite_road_labels'],
        layout: 'icons'
      }),
      {
        position: atlas.ControlPosition.BottomLeft,
      }
    );

    // Add scale control
    map.controls.add(
      new atlas.control.ScaleControl({
        maxWidth: 100,
        units: "metric",
        showLabel: true  
      }),
      {
        position: atlas.ControlPosition.BottomRight
      }
    );

    return map;
  }

};

// Export an instance of the mapService class
export const mapService = MapService.getInstance();